import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// 🔐 AUTHENTICATED API FUNCTION
export const makeAuthenticatedRequest = async (url, options = {}) => {
  // Check for token in both localStorage (persistent) and sessionStorage (session-only)
  const token = localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);
    
    // Handle authentication errors more gracefully
    if (response.status === 401) {
      console.warn('API call returned 401, but not forcing immediate logout to prevent reload loops');
      // Only remove token if multiple consecutive 401s (prevents logout loops on temporary issues)
      const failedRequests = parseInt(sessionStorage.getItem('auth_failures') || '0');
      sessionStorage.setItem('auth_failures', String(failedRequests + 1));
      
      if (failedRequests >= 3) {
        // Only logout after multiple failures
        localStorage.removeItem('subbuteo_token');
        sessionStorage.removeItem('subbuteo_token');
        localStorage.removeItem('subbuteo_remember');
        sessionStorage.removeItem('auth_failures');
        setTimeout(() => window.location.reload(), 1000); // Delayed reload
      }
      return null;
    }
    
    // Reset failure counter on successful request
    sessionStorage.removeItem('auth_failures');
    return response;
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

// 🔐 LOGIN FORM COMPONENT
export const LoginForm = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  console.log('🔍 LOGINFORM: AuthContext received', { 
    hasContext: !!authContext, 
    hasLogin: !!authContext?.login,
    contextKeys: authContext ? Object.keys(authContext) : []
  });
  
  const { login } = authContext || {};
  
  if (!login) {
    console.error('🚨 LOGINFORM: No login function available from AuthContext!');
  }
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('🔍 LOGIN: handleLogin called with:', { email: formData.email, hasPassword: !!formData.password });
    
    if (!login) {
      console.error('🚨 LOGIN ERROR: No login function available!');
      setError('Sistema di autenticazione non disponibile');
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      console.log('🔍 LOGIN: Calling login function...');
      const result = await login(formData.email, formData.password, formData.rememberMe);
      console.log('🔍 LOGIN: Got result:', result);
      
      if (!result.success) {
        console.error('🚨 LOGIN: Login failed:', result.error);
        setError(result.error);
      } else {
        console.log('✅ LOGIN: Login successful! Should redirect now...');
        // Note: Don't set loading to false if successful - let the auth state change handle the UI update
        return;
      }
    } catch (error) {
      console.error('🚨 LOGIN: Exception during login:', error);
      setError('Errore di rete');
    }
    
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return (
    <div 
      className="min-h-screen relative flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      style={{
        backgroundImage: "url('https://customer-assets.emergentagent.com/job_subbuteo-saas/artifacts/knve52ma_sfondo%20app.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      {/* Overlay for readability */}
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      
      <div className="relative sm:mx-auto sm:w-full sm:max-w-md z-10">
        {/* Logo e Titolo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-xl">
              <span className="text-5xl">⚽</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white drop-shadow-lg">
            Subbuteo Pro
          </h2>
          <p className="mt-2 text-gray-200 text-lg drop-shadow">
            Il tuo diario personale del Subbuteo
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm py-8 px-6 shadow-2xl rounded-xl sm:px-10">
          <form className="space-y-6" onSubmit={handleLogin}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
                <span className="mr-2">⚠️</span>
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  📧
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="la-tua@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  🔒
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                  Ricordami
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Password dimenticata?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Accesso in corso...
                </>
              ) : (
                <>🚀 Accedi</>
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Non hai un account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => navigate('/registrati')}
                className="w-full flex justify-center items-center py-3 px-4 border-2 border-blue-500 rounded-lg shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
              >
                ✨ Registrati Gratis
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Torna alla Home
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              🔐 Recupero Password
            </h3>
            <p className="text-gray-600 mb-4">
              Inserisci la tua email e ti invieremo le istruzioni per recuperare la password.
            </p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const email = e.target.email.value;
              try {
                const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email })
                });
                if (response.ok) {
                  alert('Se l\'email è registrata, riceverai le istruzioni per il recupero.');
                  setShowForgotPassword(false);
                }
              } catch (err) {
                console.error('Forgot password error:', err);
              }
            }}>
              <input
                type="email"
                name="email"
                placeholder="la-tua@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Invia
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// 📝 REGISTER FORM COMPONENT
export const RegisterForm = ({ inviteCode: urlInviteCode }) => {
  const navigate = useNavigate();
  const { register } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    promoCode: '',
    clubInviteCode: urlInviteCode || ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clubInviteInfo, setClubInviteInfo] = useState(null);
  const [isValidatingInvite, setIsValidatingInvite] = useState(false);

  // Validate club invite code on mount or when changed
  useEffect(() => {
    const validateInviteCode = async (code) => {
      if (!code || code.length < 3) {
        setClubInviteInfo(null);
        return;
      }

      setIsValidatingInvite(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/verify-club-code/${code}`);
        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            setClubInviteInfo(data);
          } else {
            setClubInviteInfo(null);
          }
        } else {
          setClubInviteInfo(null);
        }
      } catch (err) {
        console.error('Error validating invite code:', err);
        setClubInviteInfo(null);
      }
      setIsValidatingInvite(false);
    };

    // Debounce the validation
    const timeoutId = setTimeout(() => {
      validateInviteCode(formData.clubInviteCode);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.clubInviteCode]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setIsLoading(true);

    try {
      // First register the user
      const result = await register(formData.email, formData.name, formData.password, formData.promoCode || null);
      
      if (!result.success) {
        setError(result.error);
        setIsLoading(false);
        return;
      }

      // If there's a club invite code, activate it
      if (formData.clubInviteCode && clubInviteInfo?.valid) {
        try {
          const activateResponse = await fetch(`${BACKEND_URL}/api/activate-club-code`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('subbuteo_token')}`
            },
            body: JSON.stringify({ code: formData.clubInviteCode })
          });
          
          if (activateResponse.ok) {
            console.log('Club code activated successfully!');
          }
        } catch (err) {
          console.error('Error activating club code:', err);
          // Don't fail the registration if club activation fails
        }
      }

      // Registration successful, auth context will handle redirect
    } catch (err) {
      console.error('Registration error:', err);
      setError('Errore durante la registrazione');
    }
    
    setIsLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <div 
      className="min-h-screen relative flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      style={{
        backgroundImage: "url('https://customer-assets.emergentagent.com/job_subbuteo-saas/artifacts/knve52ma_sfondo%20app.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      {/* Overlay for readability */}
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      
      <div className="relative sm:mx-auto sm:w-full sm:max-w-md z-10">
        {/* Logo e Titolo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-xl">
              <span className="text-5xl">⚽</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white drop-shadow-lg">
            Crea il tuo Account
          </h2>
          <p className="mt-2 text-gray-200 text-lg drop-shadow">
            Inizia a tracciare le tue partite!
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm py-8 px-6 shadow-2xl rounded-xl sm:px-10">
          {/* Club Invite Preview */}
          {clubInviteInfo && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🏢</span>
                <div>
                  <p className="font-medium text-green-800">Invito al Club: {clubInviteInfo.club_name}</p>
                  {clubInviteInfo.member_name && (
                    <p className="text-sm text-green-600">Per: {clubInviteInfo.member_name}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleRegister}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
                <span className="mr-2">⚠️</span>
                {error}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Nome
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  👤
                </span>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Il tuo nome"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  📧
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="la-tua@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  🔒
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Minimo 6 caratteri"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Conferma Password
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  🔒
                </span>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ripeti la password"
                />
              </div>
            </div>

            <div>
              <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700">
                Codice Promo (opzionale)
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  🎁
                </span>
                <input
                  id="promoCode"
                  name="promoCode"
                  type="text"
                  value={formData.promoCode}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Codice sconto"
                />
              </div>
            </div>

            <div>
              <label htmlFor="clubInviteCode" className="block text-sm font-medium text-gray-700">
                Codice Invito Club (opzionale)
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                  🏢
                </span>
                <input
                  id="clubInviteCode"
                  name="clubInviteCode"
                  type="text"
                  value={formData.clubInviteCode}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full pl-10 pr-10 py-2 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    clubInviteInfo ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                  placeholder="Es: ABC123"
                />
                {isValidatingInvite && (
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                )}
                {clubInviteInfo && (
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-green-500">
                    ✓
                  </span>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Registrazione in corso...
                </>
              ) : (
                <>✨ Registrati</>
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Hai già un account?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center items-center py-3 px-4 border-2 border-green-500 rounded-lg shadow-sm text-sm font-medium text-green-600 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all"
              >
                🔐 Accedi
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Torna alla Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
